import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';
import {
  conversationCacheKey,
  conversationSessionCache,
  rememberConversation,
} from 'dashboard/helper/conversationSessionCache';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  // Aceleres: distingue "lista vazia de verdade" de "falhou ao carregar" — sem isso
  // um 500/timeout caia no empty state "nenhuma conversa" e o usuario so tinha F5.
  listErrorStatus: false,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: 'last_activity_at_desc',
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

const getMessageSortKey = message => {
  const createdAt = Number(message?.created_at);
  if (Number.isFinite(createdAt)) {
    return createdAt;
  }

  const messageId = Number(message?.id);
  if (Number.isFinite(messageId)) {
    return messageId;
  }

  return 0;
};

const sortConversationMessages = messages => {
  return [...messages].sort((left, right) => {
    const leftKey = getMessageSortKey(left);
    const rightKey = getMessageSortKey(right);

    if (leftKey === rightKey) {
      return Number(left?.id || 0) - Number(right?.id || 0);
    }

    return leftKey > rightKey ? 1 : -1;
  });
};

const mergeConversationMessages = (
  existingMessages = [],
  incomingMessages = []
) => {
  const mergedMessages = [];
  const seenMessageIds = new Set();

  [...existingMessages, ...incomingMessages].forEach(message => {
    const messageId = message?.id;

    if (messageId && seenMessageIds.has(messageId)) {
      return;
    }

    if (messageId) {
      seenMessageIds.add(messageId);
    }

    mergedMessages.push(message);
  });

  return sortConversationMessages(mergedMessages);
};

const preserveConversationLoadState = (
  incomingConversation,
  currentConversation = null,
  force = false
) => {
  const cachedConversation =
    currentConversation ||
    conversationSessionCache.get(conversationCacheKey(incomingConversation));
  const hasLoadedState =
    force ||
    cachedConversation?.dataFetched === true ||
    cachedConversation?.prefetchProgress !== undefined;
  const incomingHasLoadedState =
    incomingConversation?.dataFetched === true ||
    incomingConversation?.prefetchProgress !== undefined;

  if (!cachedConversation || (!hasLoadedState && !incomingHasLoadedState)) {
    return rememberConversation(incomingConversation);
  }

  if (incomingHasLoadedState) {
    return rememberConversation({
      ...incomingConversation,
      allMessagesLoaded:
        incomingConversation.allMessagesLoaded ??
        cachedConversation.allMessagesLoaded,
      messages: mergeConversationMessages(
        cachedConversation.messages || [],
        incomingConversation.messages || []
      ),
      dataFetched:
        incomingConversation.dataFetched ?? cachedConversation.dataFetched,
      conversationDetailsFetched:
        incomingConversation.conversationDetailsFetched ??
        cachedConversation.conversationDetailsFetched,
      prefetchProgress: Math.max(
        Number(cachedConversation.prefetchProgress || 0),
        Number(incomingConversation.prefetchProgress || 0)
      ),
    });
  }

  return rememberConversation({
    ...incomingConversation,
    allMessagesLoaded: cachedConversation.allMessagesLoaded,
    messages: cachedConversation.messages,
    dataFetched: cachedConversation.dataFetched,
    conversationDetailsFetched: cachedConversation.conversationDetailsFetched,
    prefetchProgress: cachedConversation.prefetchProgress,
  });
};

// mutations
export const mutations = {
  [types.RESTORE_CONVERSATION_FROM_SESSION_CACHE](_state, conversationId) {
    const normalizedConversationId = Number(conversationId);
    const cachedConversation = [...conversationSessionCache.values()].find(
      conversation => Number(conversation?.id) === normalizedConversationId
    );

    if (!cachedConversation) {
      return;
    }

    const indexInCurrentList = _state.allConversations.findIndex(
      conversation => conversation.id === normalizedConversationId
    );

    if (indexInCurrentList < 0) {
      _state.allConversations.push(cachedConversation);
      return;
    }

    _state.allConversations[indexInCurrentList] = preserveConversationLoadState(
      cachedConversation,
      _state.allConversations[indexInCurrentList]
    );
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(preserveConversationLoadState(conversation));
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one.
        // If the conversation was prefetched already, preserve the loaded
        // message window so the preload survives list refreshes / websocket syncs.
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = preserveConversationLoadState(
          conversation,
          existingConversation
        );
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = preserveConversationLoadState(
          conversation,
          existingConversation,
          true
        );
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.REPLACE_ALL_CONVERSATION](_state, conversationList) {
    const currentConversations = new Map(
      _state.allConversations.map(conversation => [
        conversation.id,
        conversation,
      ])
    );
    const nextConversations = conversationList.map(conversation =>
      preserveConversationLoadState(
        conversation,
        currentConversations.get(conversation.id)
      )
    );

    // A conversa aberta pode estar fora da página/caixa atual. Mantê-la no
    // store evita desmontar o histórico enquanto o usuário troca de aba; a
    // lista visual continua filtrando esse item pela visão ativa.
    const selectedConversation = _state.allConversations.find(
      conversation => conversation.id === _state.selectedChatId
    );
    if (
      selectedConversation &&
      !nextConversations.some(
        conversation => conversation.id === selectedConversation.id
      )
    ) {
      nextConversations.push(selectedConversation);
    }

    _state.allConversations = nextConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](
    _state,
    { preserveSelectedConversation = false } = {}
  ) {
    const selectedConversation = preserveSelectedConversation
      ? _state.allConversations.find(
          conversation => conversation.id === _state.selectedChatId
        )
      : null;

    _state.allConversations = selectedConversation
      ? [selectedConversation]
      : [];
    if (!selectedConversation) {
      _state.selectedChatId = null;
    }
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) return;

    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;

    const existingMessageIds = new Set((chat.messages || []).map(m => m.id));
    const messagesToAdd = data.filter(
      message => !existingMessageIds.has(message.id)
    );
    if (!messagesToAdd.length) return;

    chat.messages.unshift(...messagesToAdd);
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    if (chat.prefetchSnapshot === true) {
      chat.messages = mergeConversationMessages(chat.messages || [], data);
      return;
    }

    chat.messages = data;
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
      // The list preloader already has the conversation card data and the
      // initial message window. Avoid issuing a second show request when the
      // user opens that conversation for the first time.
      chat.conversationDetailsFetched = true;
    }
  },

  [types.SET_CONVERSATION_PREFETCH_PROGRESS](
    _state,
    { conversationId, progress }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      const normalizedProgress = Number(progress) || 0;
      chat.prefetchProgress = Math.max(0, Math.min(normalizedProgress, 100));
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId, unreadCount }
  ) {
    const [chat] = _state.allConversations.filter(
      c => Number(c.id) === Number(conversationId)
    );
    if (chat) {
      chat.last_activity_at = lastActivityAt;
      chat.timestamp = lastActivityAt;
      if (unreadCount !== undefined && unreadCount !== null) {
        chat.unread_count = Number(unreadCount) || 0;
      }
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      // Aceleres fix (2026-06-26): race condition entre fetch HTTP inicial e
      // broadcasts ActionCable de message.updated em msgs antigas (ex:
      // lib/aceleres/mark_outgoing_read_on_reply marca TODAS outgoing anteriores
      // como read quando o cliente responde -> cada update dispara message.updated
      // -> addMessage). Push cego no fim causava ordem [hoje, ontem, hoje].
      // Insere em ordem cronologica por created_at.
      const lastMessage = chat.messages[chat.messages.length - 1];
      const isNewer =
        !lastMessage ||
        new Date(message.created_at) >= new Date(lastMessage.created_at);
      if (isNewer) {
        chat.messages.push(message);
      } else {
        const insertIdx = chat.messages.findIndex(
          m => new Date(m.created_at) > new Date(message.created_at)
        );
        chat.messages.splice(insertIdx, 0, message);
      }
      // Side effects (timestamp/unread/scroll) so quando a msg e a mais nova
      if (isNewer) {
        chat.timestamp = message.created_at;
        const { conversation: { unread_count: unreadCount = 0 } = {} } =
          message;
        chat.unread_count = unreadCount;
        if (selectedChatId === conversationId) {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }

    // Keep the denormalized preview synchronized with the message collection.
    if (message.message_type !== MESSAGE_TYPE.ACTIVITY) {
      const currentPreview = chat.last_non_activity_message;
      const currentPreviewTime = currentPreview
        ? new Date(currentPreview.created_at).getTime()
        : 0;
      const messageTime = new Date(message.created_at).getTime();

      if (!currentPreview || messageTime >= currentPreviewTime) {
        chat.last_non_activity_message = message;
      }
    }
  },

  [types.UPDATE_MESSAGE_REACTIONS](
    { allConversations },
    { conversationId, messageId, reactions }
  ) {
    const chat = allConversations.find(c => c.id === conversationId);
    if (!chat) return;
    const idx = chat.messages.findIndex(m => m.id === messageId);
    if (idx === -1) return;
    const old = chat.messages[idx];
    // Substitui o item inteiro do array (mesmo padrão do ADD_MESSAGE) pra
    // forçar reatividade no allMessages computed do MessageList que faz
    // useCamelCase(deep:true) — só mutar a sub-prop pode passar batido
    // dependendo do camelcase-keys.
    chat.messages.splice(idx, 1, {
      ...old,
      content_attributes: { ...(old.content_attributes || {}), reactions },
    });
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(rememberConversation(conversation));
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    const conversation = getConversationById(_state)(conversationId);
    const cacheKey = conversationCacheKey(conversation);
    if (cacheKey) conversationSessionCache.delete(cacheKey);
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;
      allConversations[index] = rememberConversation({
        ...selectedConversation,
        ...updates,
      });
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { aceleresBox, assigneeType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      const showPersonalChats =
        aceleresBox === 'pessoais' || assigneeType === 'personal';
      // Não injetar conversas pessoais (label 'pessoal') via WebSocket —
      // elas só entram no store via carga inicial do servidor (aba Pessoais).
      const isPersonal = (conversation.labels || []).includes('pessoal');
      if (
        ![MENTION, PARTICIPATING].includes(conversationType) &&
        (showPersonalChats || !isPersonal)
      ) {
        _state.allConversations.push(rememberConversation(conversation));
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.SET_LIST_ERROR_STATUS](_state) {
    _state.listErrorStatus = true;
  },

  [types.CLEAR_LIST_ERROR_STATUS](_state) {
    _state.listErrorStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_CONVERSATION_CALL_STATUS](
    _state,
    { conversationId, callStatus }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.additional_attributes = {
      ...chat.additional_attributes,
      call_status: callStatus,
    };
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const lastCall = (chat.messages || []).findLast(
      m => m.content_type === CONTENT_TYPES.VOICE_CALL
    );

    if (!lastCall) return;

    lastCall.content_attributes ??= {};
    lastCall.content_attributes.data = {
      ...lastCall.content_attributes.data,
      status: callStatus,
    };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
